import { Combobox } from '@base-ui/react/combobox';
import { useVirtualizer, type Virtualizer } from '@tanstack/react-virtual';
import {
  type RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Checkbox } from '#components/Input/Checkbox';
import { useDropdownContext } from '../../context/DropdownContext';
import { DropdownSearch } from '../DropdownSearch/DropdownSearch';
import { DropdownGroupRow } from '../DropdownGroupRow/DropdownGroupRow';
import { DropdownItemRow } from '../DropdownItemRow/DropdownItemRow';
import { styles } from './styles';
import type { DropdownValue } from '../../types';

type VirtualizedDropdownRowsProps = {
  virtualizerRef: RefObject<Virtualizer<HTMLDivElement, Element> | null>;
};

export function VirtualizedDropdownRows({ virtualizerRef }: VirtualizedDropdownRowsProps) {
  const {
    allSelected,
    partiallySelected,
    multiple,
    searchable,
    visibleItems,
    childrenByValue,
    expandedSet,
    searchTerm,
    toggleExpanded,
    toggleAllSelections,
    searchNoResultsText,
    selectAllLabel,
    expandable,
    headerContent,
    dataTestId,
  } = useDropdownContext();
  const filteredItems = Combobox.useFilteredItems<DropdownValue>();
  const filteredItemIndexByValue = useMemo(
    () => new Map(filteredItems.map((itemValue, index) => [itemValue, index])),
    [filteredItems],
  );
  const scrollElementRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const hasSearchTerm = searchTerm.trim().length > 0;
  const showEmptyState = visibleItems.length === 0 && hasSearchTerm;
  const noResultsText =
    typeof searchNoResultsText === 'function'
      ? searchNoResultsText(searchTerm.trim())
      : searchNoResultsText;

  const virtualizer = useVirtualizer({
    count: visibleItems.length,
    getScrollElement: () => scrollElementRef.current,
    estimateSize: () => 52,
    overscan: 12,
    paddingStart: headerHeight,
    scrollPaddingStart: headerHeight,
    measureElement: (element) =>
      'offsetHeight' in element && typeof element.offsetHeight === 'number'
        ? element.offsetHeight
        : element.getBoundingClientRect().height,
  });

  useEffect(() => {
    const headerElement = headerRef.current;

    if (!headerElement) {
      setHeaderHeight(0);

      return;
    }

    const updateHeaderHeight = () => {
      setHeaderHeight(headerElement.offsetHeight);
    };

    updateHeaderHeight();

    const resizeObserver = new ResizeObserver(() => {
      updateHeaderHeight();
    });

    resizeObserver.observe(headerElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [multiple, searchable]);

  useImperativeHandle(virtualizerRef, () => virtualizer, [virtualizer]);

  const handleScrollElementRef = useCallback(
    (element: HTMLDivElement | null) => {
      scrollElementRef.current = element;

      if (element) {
        virtualizer.measure();
      }
    },
    [virtualizer],
  );

  const totalSize = Math.max(virtualizer.getTotalSize(), headerHeight + (showEmptyState ? 56 : 0));
  const setSize = filteredItems.length;
  const virtualItems = virtualizer.getVirtualItems();
  const virtualRowsStart = virtualItems[0]?.start ?? headerHeight;
  const selectAllButton = multiple ? (
    <button
      type="button"
      css={styles.SelectAllButton}
      onClick={toggleAllSelections}
      data-testid={`${dataTestId}-select-all-button`}
      data-selected={allSelected}
    >
      <div css={styles.ItemIndicator}>
        <Checkbox
          checked={allSelected}
          semiChecked={partiallySelected}
          fullWidth={true}
          stopEventPropagation={false}
        />
      </div>
      <div css={styles.ItemText}>{selectAllLabel}</div>
    </button>
  ) : null;

  return (
    <div role="presentation" ref={handleScrollElementRef} css={styles.Scroller({ totalSize })}>
      <div role="presentation" css={styles.VirtualizedPlaceholder({ totalSize })}>
        <div ref={headerRef} css={styles.VirtualizedHeader}>
          {searchable ? <DropdownSearch /> : null}
          {!hasSearchTerm && (multiple || headerContent) ? (
            <div css={styles.HeaderActions}>
              {selectAllButton}
              {headerContent}
            </div>
          ) : null}
        </div>
        {showEmptyState ? (
          <Combobox.Empty css={[styles.Empty, styles.VirtualizedEmpty({ top: headerHeight })]}>
            {noResultsText}
          </Combobox.Empty>
        ) : null}
        {virtualItems.length > 0 ? (
          <div
            css={styles.VirtualizedRows}
            style={{ transform: `translateY(${virtualRowsStart}px)` }}
            data-testid={`${dataTestId}-list`}
          >
            {virtualItems.map((virtualItem) => {
              const item = visibleItems[virtualItem.index];

              if (item.type === 'group') {
                const hasChildren = (childrenByValue.get(item.value) ?? []).length > 0;
                const isExpanded = expandedSet.has(item.value);

                return (
                  <DropdownGroupRow
                    key={virtualItem.key}
                    item={item}
                    depth={item.depth}
                    hasChildren={hasChildren}
                    isExpanded={isExpanded}
                    expandable={expandable}
                    onToggleExpanded={toggleExpanded}
                    groupRef={virtualizer.measureElement}
                    dataIndex={virtualItem.index}
                  />
                );
              }

              const itemIndex = filteredItemIndexByValue.get(item.value);

              if (itemIndex === undefined) {
                return null;
              }

              return (
                <DropdownItemRow
                  key={virtualItem.key}
                  item={item}
                  index={itemIndex}
                  ariaSetSize={setSize}
                  ariaPosInSet={itemIndex + 1}
                  itemRef={virtualizer.measureElement}
                  dataIndex={virtualItem.index}
                />
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}
